import { createContext, useCallback, useContext } from 'react'
import { Box } from '@mui/material'
import { useNavigate, useParams } from 'react-router-dom'

import { ROUTES } from '@/constants'
import { ResizableSidebarLayout } from '@/pages/User/components/ResizableSidebarLayout'

import { ReportDetailView } from './components/ReportDetail'
import { ReportsMain } from './components/ReportsMain'
import { ReportsSidebar } from './components/ReportsSidebar'
import { ReportGroup, useReportsFilters } from './hooks/useReportsFilters'
import { getReportsReportPath } from './reportsMockData'

type ReportsOutletContext = {
	groups: ReportGroup[]
	onSelectReport: (reportId: string) => void
	reportCount: number
	selectedReportId: string | null
}

const ReportsContext = createContext<ReportsOutletContext | null>(null)

export const useReportsOutlet = () => {
	const context = useContext(ReportsContext)
	if (!context) {
		throw new Error('useReportsOutlet must be used within ReportsShell')
	}
	return context
}

type ReportsShellProps = {
	onClearSelection: () => void
	onSelectReport: (reportId: string) => void
	selectedReportId: string | null
}

const ReportsShell = ({
	selectedReportId,
	onSelectReport,
	onClearSelection,
}: ReportsShellProps) => {
	const {
		search,
		setSearch,
		categories,
		types,
		selectedCategoryIds,
		selectedTypes,
		toggleCategory,
		toggleType,
		filteredReports,
		reportsByCategory,
	} = useReportsFilters()

	const outletValue: ReportsOutletContext = {
		groups: reportsByCategory,
		reportCount: filteredReports.length,
		selectedReportId,
		onSelectReport,
	}

	return (
		<ReportsContext.Provider value={outletValue}>
			<Box
				data-testid="reports-page"
				sx={{
					display: 'flex',
					height: '100%',
					minHeight: 0,
					overflow: 'hidden',
					bgcolor: '#fff',
				}}
			>
				<ResizableSidebarLayout
					defaultWidth={280}
					localStorageKey="mdd-sidebar-width-reports"
					content={
						<Box sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
							{selectedReportId ? (
								<ReportDetailView
									reportId={selectedReportId}
									onBack={onClearSelection}
								/>
							) : (
								<ReportsMain
									groups={reportsByCategory}
									reportCount={filteredReports.length}
									selectedReportId={selectedReportId}
									onSelectReport={onSelectReport}
								/>
							)}
						</Box>
					}
				>
					<ReportsSidebar
						search={search}
						setSearch={setSearch}
						categories={categories}
						types={types}
						selectedCategoryIds={selectedCategoryIds}
						selectedTypes={selectedTypes}
						selectedReportId={selectedReportId}
						filteredReports={filteredReports}
						onToggleCategory={toggleCategory}
						onToggleType={toggleType}
						onSelectReport={onSelectReport}
					/>
				</ResizableSidebarLayout>
			</Box>
		</ReportsContext.Provider>
	)
}

/** Full Reports catalog mode via `/reports` routes. */
export const ReportsLayout = () => {
	const { reportId } = useParams<{ reportId?: string }>()
	const navigate = useNavigate()

	const handleSelectReport = useCallback(
		(id: string) => {
			navigate(getReportsReportPath(id))
		},
		[navigate],
	)

	const handleClearSelection = useCallback(() => {
		navigate(ROUTES.REPORTS)
	}, [navigate])

	return (
		<ReportsShell
			selectedReportId={reportId ?? null}
			onSelectReport={handleSelectReport}
			onClearSelection={handleClearSelection}
		/>
	)
}
